// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import { Page } from "react-page-layout";

import serviceActionCreators from "@modules/service/actionCreator";
import type { WellbeingService } from "@modules/service/reducer/wellbeing-services/types";
import type { State } from "@store/reducers";
import { getUserId } from "@modules/account/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import { formatDateTime } from "@transformers/date";
import withErrorHandler from "@components/error";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import {
  AddWellbeingServicesModal,
  EditWellbeingServicesModal,
  HelpPanelService,
} from "@modules/service/components";
import {
  Button,
  ButtonGroup,
  Loader,
  GSGrid,
  SectionComponent,
  TooltipDropdown,
  RightPanel,
  View,
} from "@components";

type PropsFromState = $Exact<{
  currentUserId: number | null,
  isLoading?: boolean,
  list: Array<WellbeingService>,
  showAddModal: boolean,
  showEditModal: boolean,
  currentDetails: ?WellbeingService,
}>;

type PropsFromDispatch = $Exact<{
  getList: typeof serviceActionCreators.GetWellbeingServicesList,
  setShowAddModal: typeof serviceActionCreators.SetShowAddWellbeingServiceModal,
  setShowEditModal: typeof serviceActionCreators.SetShowEditWellbeingServiceModal,
  setWellbeingServiceDraft: typeof serviceActionCreators.SetWellbeingServiceDraft,
  setWellbeingServiceCurrentDetails: typeof serviceActionCreators.SetWellbeingServiceCurrentDetails,
  newAssignments: typeof serviceActionCreators.NewWellbeingServiceAssignments,
  getDetails: typeof serviceActionCreators.GetWellbeingServiceDetails,
  push: typeof push,
}>;

type Props = $Exact<{
  clientId: number,
  isEnabled: boolean,
  ...PropsFromPermissions,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type FilterState = "ALL" | "ACTIVE" | "INACTIVE";

type OwnState = $Exact<{
  filter: FilterState,
  isPanelOpened: boolean,
}>;

export class WellbeingServicesTable extends React.Component<Props, OwnState> {
  state = {
    filter: "ALL",
    isPanelOpened: false,
  };

  componentDidMount() {
    this.props.getList({ clientId: this.props.clientId });
  }

  componentDidUpdate(prevProps: Props) {
    // always clear the current and draft objects after add or edit
    if (
      this.props.currentDetails &&
      this.props.currentDetails.id !== prevProps.currentDetails?.id
    ) {
      this.props.setWellbeingServiceDraft(this.props.currentDetails?.content);
      this.props.setShowEditModal({ show: true, step: 1 });
    }
  }

  get data() {
    return this.props.list.filter(d => {
      if (this.state.filter !== "ALL") {
        return d && d.content.isActive === (this.state.filter === "ACTIVE");
      }

      return d;
    });
  }

  get columns() {
    return [
      {
        name: this.props.t("ID"),
        key: "id",
      },
      {
        name: this.props.t("PROVIDER_NAME"),
        key: "name",
        renderCell: ({ row }: { row: WellbeingService }) => row.content.name,
        align: "left",
        minWidth: 140,
      },
      {
        name: this.props.t("SERVICE_TYPES"),
        key: "wellbeingServiceTypes",
        align: "left",
        width: "fit-content",
        renderCell: ({ row }: { row: WellbeingService }) =>
          GSGrid.render.ellipsis(
            (row.content.wellbeingServiceTypes || []).map(i => i.name).join(", "),
          ),
      },
      {
        name: this.props.t("EAP_DETAILS_HIERARCHY_SCOPE"),
        key: "hierarchyAssignments",
        renderCell: ({ row }: { row: WellbeingService }) =>
          (row.hierarchyAssignments || []).map(assignment => assignment.label).join(", "),
        align: "left",
      },
      {
        name: this.props.t("DATE_CREATED"),
        key: "createdAt",
        renderCell: ({ row }: { row: WellbeingService }) => formatDateTime(row.createdAt),
      },
      {
        name: this.props.t("ADDED_BY"),
        key: "addedBy",
        renderCell: ({ row }: { row: WellbeingService }) =>
          row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : null,
        align: "left",
      },
      {
        name: this.props.t("STATUS"),
        key: "status",
        minWidth: 120,
        frozen: "right",
        renderCell: ({ row }: { row: WellbeingService }) =>
          GSGrid.render.statusPill(this.props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
      },
      {
        name: this.props.t("EDIT"),
        frozen: "right",
        minWidth: 80,
        onCellClick: (row: WellbeingService) => this.openDetails(row),
        renderCell: ({ row }: { row: WellbeingService }) => GSGrid.render.materialIcon("edit"),
      },
    ];
  }

  openDetails = (data: WellbeingService) => {
    // clear the state first in case that old data wasn't removed
    this.props.setWellbeingServiceDraft(null);
    this.props.setWellbeingServiceCurrentDetails();

    // get details
    this.props.getDetails({
      clientId: this.props.clientId,
      providerId: data.id,
    });
  };

  handleAddSetup = () => {
    this.props.setWellbeingServiceDraft();
    this.props.setWellbeingServiceCurrentDetails();
    this.props.newAssignments();
    this.props.setShowAddModal({ show: true, step: 1 });
  };

  handleChangeFilter = (filter: FilterState) => () => {
    this.setState({ filter });
  };

  toggleRightPanel = () => {
    this.setState({ isPanelOpened: !this.state.isPanelOpened });
  };

  render() {
    return (
      <View gap="6">
        {this.props.isLoading && <Loader />}
        {this.state.isPanelOpened && (
          <RightPanel title={this.props.t("SERVICES")} onClose={this.toggleRightPanel}>
            <HelpPanelService />
          </RightPanel>
        )}
        <View direction="row" className="message-box">
          {this.props.t("WELLBEING_SERVICES_PAGE_DESCRIPTION")}
          <Button className="ml-3" onClick={this.toggleRightPanel} materialIcon="help">
            {this.props.t("HELP")}
          </Button>
        </View>

        <SectionComponent wrapperClassName="flex-column" className="gs-container">
          <div className="d-flex justify-content-between mb-3">
            <ButtonGroup className="mb-4">
              <Button
                color={this.state.filter === "ALL" ? "selected" : null}
                onClick={this.handleChangeFilter("ALL")}
              >
                {this.props.t("ALL")}
              </Button>
              <Button
                color={this.state.filter === "ACTIVE" ? "selected" : null}
                onClick={this.handleChangeFilter("ACTIVE")}
              >
                {this.props.t("ACTIVE")}
              </Button>
              <Button
                color={this.state.filter === "INACTIVE" ? "selected" : null}
                onClick={this.handleChangeFilter("INACTIVE")}
              >
                {this.props.t("INACTIVE")}
              </Button>
            </ButtonGroup>
            <Button onClick={this.handleAddSetup} materialIcon="add_box">
              {this.props.t("ADD")}
            </Button>
          </div>

          <GSGrid columns={this.columns} rows={this.data} maxHeight="100%" />
        </SectionComponent>
        {this.props.showAddModal && (
          <AddWellbeingServicesModal clientId={this.props.clientId} defaultStep={1} />
        )}
        {this.props.showEditModal && (
          <EditWellbeingServicesModal clientId={this.props.clientId} defaultStep={1} />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    currentUserId: getUserId(state),
    isLoading: serviceSelectors.isLoading(state),
    list: serviceSelectors.wellbeingServicesList(state),
    showAddModal: serviceSelectors.getShowWellbeingAddModal(state),
    showEditModal: serviceSelectors.getShowEditModal(state),
    currentDetails: serviceSelectors.getCurrentWellbeingServiceDetails(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getList: serviceActionCreators.GetWellbeingServicesList,
  setShowAddModal: serviceActionCreators.SetShowAddWellbeingServiceModal,
  setShowEditModal: serviceActionCreators.SetShowEditWellbeingServiceModal,
  setWellbeingServiceDraft: serviceActionCreators.SetWellbeingServiceDraft,
  setWellbeingServiceCurrentDetails: serviceActionCreators.SetWellbeingServiceCurrentDetails,
  newAssignments: serviceActionCreators.NewWellbeingServiceAssignments,
  getDetails: serviceActionCreators.GetWellbeingServiceDetails,
  push,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({
    permissionId: PermissionEnum.ACCESS_ADMIN_SERVICE,
  }),
)(WellbeingServicesTable);
